/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './LanguageSelect';

const LanguageSelectManager: ComponentManagerFn<'LanguageSelect'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'LanguageSelect'>(platform, 'LanguageSelect'),
    type: 'LanguageSelect',
    name: 'Language Select',
    defaultValue,
    props: {
      displayIcon: {
        label: 'Display Language Icon',
        type: 'boolean',
      },
    },
    category: 'Forms',
    icon: { iconName: 'language' },
    description: `**Overview**<br/>
The Language Select component provides a dropdown menu that allows users to switch the application's interface language. It is a key accessibility and localization tool, enabling users to instantly translate the UI into their preferred language.
Use this component when building **Custom Headers** or specialized settings pages where you need to offer manual language controls.

**1. Integration Context**<br/>
Like the Full Screen Toggle, this is a utility component often found in global navigation bars.
* **Standard Behavior:** The default application header already includes a language picker.
* **When to Use:** Add this component only if you have hidden the default header and are designing a custom top bar or a "User Preferences" profile page.

**2. Configuration**<br/>
The component is largely pre-configured to work with the system's defined locales.
* **Display Language Icon:** A boolean toggle.
    * **Checked:** Displays a small flag or globe icon next to the language name for visual recognition.
    * **Unchecked:** Displays only the text name of the language (e.g., "English", "Español").

**3. Best Use Cases**<br/>
* **Custom Global Headers:** Replicating standard navigation functionality in a branded header.
* **Login Screens:** Allowing users to switch languages **before** they log in.
* **Profile Settings:** Letting users set their preferred language in a "My Account" section.
* **Kiosks:** Providing large, accessible language buttons for public terminals.`,
  };
};

export default LanguageSelectManager;
